import React, { useState } from 'react';
import { Calendar, GiftIcon, CalendarCheck } from 'lucide-react';
import { formatDate } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для отображения праздников России
 */
const HolidaysSidebar = ({ 
  russianHolidays, 
  formData 
}) => {
  const [showAllHolidays, setShowAllHolidays] = useState(false);
  
  // Группируем праздники по месяцам
  const holidaysByMonth = russianHolidays.reduce((acc, holiday) => {
    const date = new Date(holiday.date);
    const month = date.getMonth();
    
    if (!acc[month]) {
      acc[month] = [];
    }
    
    acc[month].push({
      ...holiday,
      day: date.getDate()
    });
    
    return acc;
  }, {});
  
  // Названия месяцев
  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];
  
  // Определяем, какие праздники показывать (все или только ближайшие)
  const displayHolidays = showAllHolidays 
    ? russianHolidays 
    : russianHolidays.slice(0, 5); // Только первые 5 праздников
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <GiftIcon className="w-5 h-5 mr-2 text-cyan-500" />
        Праздники России
      </h3>
      
      {formData.excludeHolidays && formData.useRussianHolidays ? (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-100 dark:border-green-800 text-sm text-green-700 dark:text-green-300">
          <div className="flex items-start">
            <CalendarCheck className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              Российские праздники учитываются в ваших расчетах и будут исключены из рабочих дней.
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded border border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-start">
            <Calendar className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              Включите опцию "Исключить праздники" и "Использовать российские праздники" в настройках расчета, чтобы учитывать эти даты.
            </div>
          </div>
        </div>
      )}
      
      {/* Отображение праздников по месяцам */}
      {showAllHolidays ? (
        <div className="space-y-4">
          {Object.entries(holidaysByMonth).map(([month, holidays]) => (
            <div key={month} className="border-b border-gray-200 dark:border-gray-700 pb-3 last:border-0">
              <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                {monthNames[month]}
              </h4>
              <ul className="space-y-1.5">
                {holidays.map((holiday, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-sm font-medium text-cyan-600 dark:text-cyan-400 mr-2 w-5">
                      {holiday.day}
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {holiday.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <div>
          <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
            Ближайшие праздники:
          </h4>
          <ul className="space-y-1.5 mb-3">
            {displayHolidays.map((holiday, index) => (
              <li key={index} className="flex items-start">
                <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">
                  {formatDate(holiday.date, 'short')}:
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {holiday.name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Кнопка Показать все/меньше */}
      <button
        type="button"
        onClick={() => setShowAllHolidays(!showAllHolidays)}
        className="mt-3 text-sm text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300"
      >
        {showAllHolidays ? 'Показать меньше' : 'Показать все праздники'}
      </button>
      
      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        <p>* Информация о государственных праздниках предоставлена в соответствии с Трудовым кодексом Российской Федерации.</p>
      </div>
    </div>
  );
};

export default HolidaysSidebar;